import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trash2, ShieldAlert, KeyRound, HelpCircle, ShieldCheck, FileText, Mail, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { useI18n } from "@/i18n/I18nContext";
import { translateApiError } from "@/utils/apiErrors";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { DASHBOARD } from "@/constants/testIds";
import VoiceRecorder from "@/components/voice/VoiceRecorder";

const COUNTRIES = ["FR", "BE", "CH", "CA", "LU", "US", "GB", "DE", "ES", "IT", "Autre"];

export default function ProfileTab() {
  const { t, language, setLanguage } = useI18n();
  const { user, updateProfile, changePassword, deleteAccount } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || "");
  const [country, setCountry] = useState(user?.country || "");
  const [saving, setSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isEmailAuth = user?.auth_provider === "email";

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({ name, language_pref: language, country });
      toast({ title: t("common.success") });
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      toast({ title: t("dashboard.passwordChanged") });
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await deleteAccount(deletePassword);
      toast({ title: t("dashboard.accountDeleted") });
      navigate("/");
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) });
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-md space-y-8">
      <div className="space-y-5 rounded-2xl border border-border bg-card p-6">
        <div>
          <Label>{t("dashboard.profileEmail")}</Label>
          <Input value={user?.email || ""} disabled className="mt-1.5 rounded-xl" />
        </div>
        <div>
          <Label>{t("dashboard.profileName")}</Label>
          <Input data-testid={DASHBOARD.profileNameInput} value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 rounded-xl" />
        </div>
        <div>
          <Label>{t("dashboard.profileLanguage")}</Label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="mt-1.5 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="en">English</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("dashboard.profileCountry")}</Label>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger data-testid={DASHBOARD.profileCountrySelect} className="mt-1.5 rounded-xl">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button className="w-full rounded-full" data-testid={DASHBOARD.profileSaveBtn} onClick={handleSave} disabled={saving}>
          {saving ? t("common.loading") : t("dashboard.saveProfile")}
        </Button>
      </div>

      {isEmailAuth && (
        <div className="space-y-4 rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" />
            <h3 className="font-heading text-sm font-semibold">{t("dashboard.profileSecurityTitle")}</h3>
          </div>
          <div>
            <Label>{t("dashboard.profileCurrentPassword")}</Label>
            <Input
              data-testid={DASHBOARD.profileCurrentPasswordInput}
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="mt-1.5 rounded-xl"
            />
          </div>
          <div>
            <Label>{t("dashboard.profileNewPassword")}</Label>
            <Input
              data-testid={DASHBOARD.profileNewPasswordInput}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1.5 rounded-xl"
            />
          </div>
          <Button
            variant="outline"
            className="w-full rounded-full"
            data-testid={DASHBOARD.profileChangePasswordBtn}
            onClick={handleChangePassword}
            disabled={changingPassword || !currentPassword || newPassword.length < 6}
          >
            {changingPassword ? t("common.loading") : t("dashboard.changePassword")}
          </Button>
        </div>
      )}

      <div className="space-y-4 rounded-2xl border border-border bg-card p-6">
        <div>
          <h3 className="font-heading text-sm font-semibold">{t("dashboard.voiceProfileTitle")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.voiceProfileDesc")}</p>
        </div>
        <VoiceRecorder />
      </div>

      <div className="space-y-1 rounded-2xl border border-border bg-card p-6">
        <h3 className="mb-3 font-heading text-sm font-semibold">{t("dashboard.helpLegalTitle")}</h3>
        {[
          { to: "/faq", icon: HelpCircle, label: t("nav.faq") },
          { to: "/confidentialite", icon: ShieldCheck, label: t("legalDocs.privacyNav") },
          { to: "/cgu", icon: FileText, label: t("legalDocs.termsNav") },
          { to: "/cgv", icon: FileText, label: t("legalDocs.salesNav") },
          { to: "/contact", icon: Mail, label: t("legalDocs.contactNav") },
        ].map(({ to, icon: Icon, label }) => (
          <Link
            key={to}
            to={to}
            data-testid={DASHBOARD.profileHelpLink(to)}
            className="flex items-center justify-between rounded-xl px-2 py-2.5 text-sm text-foreground transition-colors hover:bg-accent/10"
          >
            <span className="flex items-center gap-2.5">
              <Icon className="h-4 w-4 text-primary" />
              {label}
            </span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Link>
        ))}
      </div>

      <div className="space-y-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-6">
        <div className="flex items-center gap-2 text-destructive">
          <ShieldAlert className="h-4 w-4" />
          <h3 className="font-heading text-sm font-semibold">{t("dashboard.dangerZoneTitle")}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t("dashboard.deleteAccountWarning")}</p>
        <Button
          variant="destructive"
          className="w-full rounded-full"
          data-testid={DASHBOARD.profileDeleteAccountBtn}
          onClick={() => setDeleteOpen(true)}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {t("dashboard.deleteAccountBtn")}
        </Button>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dashboard.deleteAccountConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("dashboard.deleteAccountConfirmDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          {isEmailAuth && (
            <div>
              <Label>{t("dashboard.deleteAccountConfirmPassword")}</Label>
              <Input
                data-testid={DASHBOARD.profileDeleteConfirmPasswordInput}
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="mt-1.5 rounded-xl"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletePassword("")}>{t("common.cancel")}</AlertDialogCancel>
            <Button
              variant="destructive"
              className="rounded-full"
              data-testid={DASHBOARD.profileDeleteConfirmBtn}
              onClick={handleDeleteAccount}
              disabled={deleting || (isEmailAuth && !deletePassword)}
            >
              {deleting ? t("common.loading") : t("dashboard.deleteAccountConfirmBtn")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
